import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, notification } from 'antd';
import { collection } from 'firebase/firestore';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import ActionButton from '@src/components/Buttons/ActionButton';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import type { ResponsiveTableColumn } from '@src/components/Table';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { useFirestoreListSync } from '@src/hooks/useFirestoreListSync';
import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import { AttendanceDashboardMapper } from '@src/models/mappers/attendanceDashboard.mapper';
import {
  getRealtimeStatusFilterCodesForTab,
  WORK_JOURNEY_REAL_TIME_TAB,
  type WorkJourneyRealTimeTab,
} from '@src/packages/workJourney/constants';
import { useElasticQueryClient, useFirestoreQuery } from '@src/packages/utils';
import { DateUtil } from '@src/packages/utils/dates';
import { useRulesByCountry } from '@src/packages/utils/hooks/useRulesByCountry';
import AttendanceDashboardService, {
  ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE,
} from '@src/services/AttendanceDashboardService';
import ExceptionService from '@src/services/ExceptionService';
import WorkJourneyManagementService from '@src/services/WorkJourneyManagementService';

import WorkJourneyArrivalDelayCell from '../components/WorkJourneyArrivalDelayCell';
import WorkJourneyRealTimeUserActions from '../components/WorkJourneyRealTimeUserActions';
import {
  buildJourneyAttendanceRows,
  computeAttendanceKpis,
  getJourneyInstanceMembers,
  matchesJourneyInstance,
  toUserStatusInput,
} from '../utils/attendanceMetrics';
import {
  getAttendanceStatusLabelId,
  resolveUserRowAttendanceState,
} from '../utils/attendanceStatus/resolveAttendanceStatus';
import type {
  AttendanceKpis,
  AttendanceStatusDisplay,
  JourneyAttendanceGroupRow,
} from '../utils/attendanceStatus/types';
import { buildReportAbsenceCandidates } from '../utils/buildReportAbsenceCandidates';
import {
  buildWorkJourneyReplacementTargets,
  type WorkJourneyReplacementTarget,
} from '../utils/buildWorkJourneyReplacementTargets';
import { buildCurrentDayRange, timestampSecondsOrNull } from '../utils/buildCurrentDayRange';
import { renderAttendanceStatusTag } from '../utils/renderAttendanceStatusTag';
import {
  applyStructuralFilters,
  buildRealTimeFilterFacetOptions,
  filterAttendanceItems,
  isTimeAwareRealTimeStatusFilter,
  resolveConsistentRealTimeSelection,
  toFilterString,
  type FilterSelectOption,
} from '../utils/filterAttendanceItems';
import {
  INIT_WORK_JOURNEY_REAL_TIME_FILTERS,
  mergeRealTimeStructuralSelection,
  REAL_TIME_FILTER_FIELD,
  STRUCTURAL_FILTER_LABEL_IDS,
} from '../utils/initRealTimeFilters';
import {
  REAL_TIME_KPI_FILTERS,
  resolveActiveKpiKey,
  resolveKpiFilterSelection,
  type RealTimeKpiKey,
} from '../utils/realTimeKpiFilters';

import styles from '../WorkJourneyRealTime.module.less';

const PAGE_SIZE = 10;

export type { WorkJourneyRealTimeTab };

interface WorkShiftRecord {
  id: string;
  name?: string;
  regulationName?: string;
}

const mergeSyncedAttendanceItems = (
  items: AttendanceDashboardItem[],
  syncedItemsById: ReadonlyMap<string, AttendanceDashboardItem>,
): AttendanceDashboardItem[] => items.map(item => syncedItemsById.get(item.id) ?? item);

export interface UseWorkJourneyRealTimeReturn {
  loading: boolean;
  isLoaded: boolean;
  error: string | null;
  isRangeTruncated: boolean;
  rangeTotalAvailable: number;
  rangeMaxRecords: number;
  activeTab: WorkJourneyRealTimeTab;
  setActiveTab: (tab: WorkJourneyRealTimeTab) => void;
  currentPage: number;
  pageSize: number;
  totalCount: number;
  handlePageChange: (page: number) => void;
  kpis: AttendanceKpis;
  activeKpiKey: RealTimeKpiKey | null;
  handleSelectKpi: (key: RealTimeKpiKey) => void;
  userColumns: ResponsiveTableColumn<AttendanceDashboardItem>[];
  journeyColumns: ResponsiveTableColumn<JourneyAttendanceGroupRow>[];
  userTableData: AttendanceDashboardItem[];
  /** Full filtered day window (not paginated) — used by general-fix flows. */
  filteredItems: AttendanceDashboardItem[];
  /** Unfiltered day window (Firestore overlays applied) — journey labels for Mark Attendance. */
  dayItems: AttendanceDashboardItem[];
  journeyTableData: JourneyAttendanceGroupRow[];
  toggleFilters: () => void;
  isFilterPanelOpen: boolean;
  filters: ReturnType<typeof useSecondaryPanel>['values'];
  setFilters: ReturnType<typeof useSecondaryPanel>['setValue'];
  handleStructuralFilterChange: (
    field:
      | typeof REAL_TIME_FILTER_FIELD.WORK_SHIFT
      | typeof REAL_TIME_FILTER_FIELD.WORK_JOURNEY
      | typeof REAL_TIME_FILTER_FIELD.USER,
    value: string,
  ) => void;
  shiftOptions: FilterSelectOption[];
  journeyOptions: FilterSelectOption[];
  userOptions: FilterSelectOption[];
  replacementTargets: WorkJourneyReplacementTarget[];
  reportAbsenceUsers: AttendanceDashboardItem[];
  handleReportAbsence: (
    user: AttendanceDashboardItem,
    payload: ReportAbsencePayload,
  ) => Promise<boolean>;
  refresh: () => void;
}

export interface UseWorkJourneyRealTimeParams {
  onMarkUser?: (item: AttendanceDashboardItem) => void;
  onViewJourneyUsers?: (items: AttendanceDashboardItem[]) => void;
  onViewMarkedJourneyUsers?: (items: AttendanceDashboardItem[]) => void;
  onReplaceUser?: (item: AttendanceDashboardItem) => void;
  onReportAbsence?: (item: AttendanceDashboardItem) => void;
}

export type { WorkJourneyReplacementTarget };

export type ReportAbsencePayload = {
  excuse: 'justified_absence' | 'unjustified_absence';
  reasonType: string | null;
  description: string;
};

export const useWorkJourneyRealTime = ({
  onMarkUser,
  onViewJourneyUsers,
  onViewMarkedJourneyUsers,
  onReplaceUser,
  onReportAbsence,
}: UseWorkJourneyRealTimeParams = {}): UseWorkJourneyRealTimeReturn => {
  const intl = useIntl();
  const { countryRulesForm } = useRulesByCountry();
  const orgId = useSelector(
    (state: { organizations: { organization: { id: string } } }) =>
      state.organizations.organization.id,
  );
  const {
    values: filters,
    setValue: setFilters,
    toggle: toggleFilters,
    setValues,
    isOpen: isFilterPanelOpen,
  } = useSecondaryPanel();
  const [activeTab, setActiveTab] = useState<WorkJourneyRealTimeTab>(
    WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [nowMinute, setNowMinute] = useState(() => Math.floor(Date.now() / 60_000));
  const [isRangeTruncated, setIsRangeTruncated] = useState(false);
  const [rangeTotalAvailable, setRangeTotalAvailable] = useState(0);
  const rangeTruncationRef = useRef({
    truncated: false,
    totalAvailable: 0,
  });

  const range = useMemo(() => buildCurrentDayRange(), []);

  const attendanceDashboardServiceRef = useRef<AttendanceDashboardService | null>(null);
  if (attendanceDashboardServiceRef.current === null) {
    attendanceDashboardServiceRef.current = new AttendanceDashboardService();
  }
  const attendanceDashboardService = attendanceDashboardServiceRef.current;
  const workJourneyManagementServiceRef = useRef<WorkJourneyManagementService | null>(null);
  if (workJourneyManagementServiceRef.current === null) {
    workJourneyManagementServiceRef.current = new WorkJourneyManagementService();
  }
  const workJourneyManagementService = workJourneyManagementServiceRef.current;
  const exceptionServiceRef = useRef<ExceptionService | null>(null);
  if (exceptionServiceRef.current === null) {
    exceptionServiceRef.current = new ExceptionService();
  }

  const {
    data: elasticItems,
    loading: elasticLoading,
    isFetching,
    error,
    refetch,
  } = useElasticQueryClient<AttendanceDashboardItem>({
    queryKey: [orgId, range.gte, range.lt, range.time_zone],
    queryFn: async () => {
      const result = await attendanceDashboardService.listAllInRange({
        organizationId: orgId,
        range,
      });
      rangeTruncationRef.current = {
        truncated: Boolean(result.truncated),
        totalAvailable: result.totalAvailable ?? result.total,
      };
      return { items: result.items, total: result.total };
    },
    onSuccess: () => {
      setIsRangeTruncated(rangeTruncationRef.current.truncated);
      setRangeTotalAvailable(rangeTruncationRef.current.totalAvailable);
    },
    onError: () => {
      setIsRangeTruncated(false);
      setRangeTotalAvailable(0);
    },
    enabled: !!orgId,
  });

  const getAttendanceRecordDocPath = useCallback(
    (item: AttendanceDashboardItem): string | null =>
      item?.id && orgId
        ? `${FSCollections.ORGANIZATIONS}/${orgId}/${FSCollections.ATTENDANCE_RECORDS}/${item.id}`
        : null,
    [orgId],
  );

  const shiftQuery = useMemo(
    () => collection(db, FSCollections.ORGANIZATIONS, orgId, FSCollections.WORK_SHIFTS),
    [orgId],
  );
  const { data: workShifts = [] } = useFirestoreQuery(shiftQuery, [orgId]);

  const regulationNameByShiftId = useMemo(() => {
    const map = new Map<string, string>();
    (workShifts as WorkShiftRecord[]).forEach(shift => {
      const regulationName = shift?.regulationName?.trim();
      if (shift?.id && regulationName) {
        map.set(shift.id, regulationName);
      }
    });
    return map;
  }, [workShifts]);
  const shiftNameById = useMemo(() => {
    const map = new Map<string, string>();
    (workShifts as WorkShiftRecord[]).forEach(shift => {
      const name = shift?.name?.trim();
      if (shift?.id && name) {
        map.set(shift.id, name);
      }
    });
    return map;
  }, [workShifts]);

  /**
   * Full current-day window enriched with shift names — used for KPIs, journey
   * grouping, and filter options. Day bounds come from the Elastic date-math
   * range; Firestore sync is applied only to the visible page (see pageItemsForSync).
   */
  const enrichedItems = useMemo(
    () =>
      (elasticItems ?? []).map(item => ({
        ...item,
        regulationName: item.workShiftId
          ? regulationNameByShiftId.get(item.workShiftId) ?? null
          : null,
      })),
    [elasticItems, regulationNameByShiftId],
  );

  /**
   * Elastic hits whose Firestore attendance docs no longer exist. Accumulated as
   * page-scoped listeners discover deletions so pagination/KPIs don't count ghosts.
   */
  const [knownDeletedIds, setKnownDeletedIds] = useState<Set<string>>(() => new Set());
  const [firestoreOverlayById, setFirestoreOverlayById] = useState(
    () => new Map<string, AttendanceDashboardItem>(),
  );
  const pageItemsForSyncRef = useRef<AttendanceDashboardItem[]>([]);

  useEffect(() => {
    setKnownDeletedIds(new Set());
    setFirestoreOverlayById(new Map());
  }, [elasticItems]);

  const dayWindowItems = useMemo(
    () => enrichedItems.filter(item => !knownDeletedIds.has(item.id)),
    [enrichedItems, knownDeletedIds],
  );

  const workJourneyConfigIds = useMemo(
    () =>
      Array.from(
        new Set(
          dayWindowItems
            .map(item => item.workJourneyConfigId)
            .filter((id): id is string => Boolean(id)),
        ),
      ).sort(),
    [dayWindowItems],
  );

  const {
    data: staffingSummaries,
    loading: staffingLoading,
    isFetching: isStaffingFetching,
    refetch: refetchStaffing,
  } = useElasticQueryClient({
    // Generic fetch cache (not Elastic). Roster counts come from Firestore.
    queryKey: [orgId, workJourneyConfigIds],
    queryFn: async () => {
      const items = await workJourneyManagementService.listWorkJourneyConfigStaffing(
        orgId,
        workJourneyConfigIds,
      );
      return { items, total: items.length };
    },
    enabled: Boolean(orgId) && workJourneyConfigIds.length > 0,
    keepPreviousData: true,
  });

  const staffingByConfigId = useMemo(
    () =>
      new Map(
        staffingSummaries.map(summary => [summary.workJourneyConfigId, summary] as const),
      ),
    [staffingSummaries],
  );

  useEffect(() => {
    setValues(INIT_WORK_JOURNEY_REAL_TIME_FILTERS);
  }, [setValues]);

  const filterWorkShiftId = toFilterString(
    filters?.[REAL_TIME_FILTER_FIELD.WORK_SHIFT]?.value,
  );
  const filterWorkJourneyConfigId = toFilterString(
    filters?.[REAL_TIME_FILTER_FIELD.WORK_JOURNEY]?.value,
  );
  const filterEmployeeId = toFilterString(filters?.[REAL_TIME_FILTER_FIELD.USER]?.value);
  const filterStatus = toFilterString(filters?.[REAL_TIME_FILTER_FIELD.STATUS]?.value);
  const isTimeAwareStatusFilter = isTimeAwareRealTimeStatusFilter(filterStatus, activeTab);
  const statusFilterNowMinute = isTimeAwareStatusFilter ? nowMinute : null;

  useEffect(() => {
    if (!isTimeAwareStatusFilter) {
      return undefined;
    }

    const tick = (): void => setNowMinute(Math.floor(Date.now() / 60_000));
    tick();
    const intervalId = window.setInterval(tick, 60_000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isTimeAwareStatusFilter]);

  const attendanceListFilters = useMemo(
    () => ({
      workShiftId: filterWorkShiftId,
      workJourneyConfigId: filterWorkJourneyConfigId,
      employeeId: filterEmployeeId,
      status: filterStatus,
      statusScope: activeTab,
      now:
        statusFilterNowMinute === null
          ? undefined
          : new Date(statusFilterNowMinute * 60_000),
    }),
    [
      activeTab,
      filterEmployeeId,
      filterStatus,
      filterWorkJourneyConfigId,
      filterWorkShiftId,
      statusFilterNowMinute,
    ],
  );

  /**
   * Overlay-aware membership for which docs to listen to (attended → absent
   * stays on the Absent KPI page). Overlay itself is applied only in
   * `overlaidDayItems` for the table and KPIs.
   */
  const overlayAwareFilteredItems = useMemo(
    () =>
      filterAttendanceItems(
        mergeSyncedAttendanceItems(dayWindowItems, firestoreOverlayById),
        attendanceListFilters,
        staffingByConfigId,
      ),
    [attendanceListFilters, dayWindowItems, firestoreOverlayById, staffingByConfigId],
  );

  const overlayAwareJourneyRows = useMemo(
    () => buildJourneyAttendanceRows(overlayAwareFilteredItems, staffingByConfigId),
    [overlayAwareFilteredItems, staffingByConfigId],
  );

  const handleReportAbsence = useCallback(
    async (user: AttendanceDashboardItem, payload: ReportAbsencePayload): Promise<boolean> => {
      if (!orgId) return false;

      const scheduledStartAtMs = DateUtil.getTimestampMs(user.scheduledStartAt);
      if (scheduledStartAtMs === null) return false;

      const scheduledDate = DateUtil.toDateString(
        scheduledStartAtMs,
        'YYYY-MM-DD',
        DateUtil.getDefaultTimeZone(),
      );
      const type =
        payload.excuse === 'unjustified_absence'
          ? 'unjustified_absence'
          : payload.reasonType || 'justified_absence';

      try {
        await exceptionServiceRef.current!.createException(
          {
            type,
            isExcused: payload.excuse === 'justified_absence',
            startDate: scheduledDate,
            endDate: scheduledDate,
            user: {
              id: user.employeeId,
              firstName: user.userFirstName ?? '',
              lastName: user.userLastName ?? '',
            },
            description: payload.description || undefined,
          },
          orgId,
        );
        notification.success({
          message: intl.formatMessage({ id: 'general.save.successful.message' }),
        });
        return true;
      } catch (error) {
        console.error('Error reporting work journey absence:', error);
        notification.error({
          message: intl.formatMessage({ id: 'general.save.error.message' }),
        });
        return false;
      }
    },
    [intl, orgId],
  );

  const optionSourceItems = dayWindowItems;
  const { shiftOptions, journeyOptions, userOptions } = useMemo(
    () =>
      buildRealTimeFilterFacetOptions(
        optionSourceItems,
        {
          workShiftId: filterWorkShiftId,
          workJourneyConfigId: filterWorkJourneyConfigId,
          employeeId: filterEmployeeId,
        },
        shiftNameById,
      ),
    [
      filterEmployeeId,
      filterWorkJourneyConfigId,
      filterWorkShiftId,
      optionSourceItems,
      shiftNameById,
    ],
  );

  const structuralFieldLabels = useMemo(
    () => ({
      workShiftId: intl.formatMessage({
        id: STRUCTURAL_FILTER_LABEL_IDS[REAL_TIME_FILTER_FIELD.WORK_SHIFT],
      }),
      workJourneyConfigId: intl.formatMessage({
        id: STRUCTURAL_FILTER_LABEL_IDS[REAL_TIME_FILTER_FIELD.WORK_JOURNEY],
      }),
      employeeId: intl.formatMessage({
        id: STRUCTURAL_FILTER_LABEL_IDS[REAL_TIME_FILTER_FIELD.USER],
      }),
    }),
    [intl],
  );

  const getStructuralTranslatedValues = useCallback(
    (selection: {
      workShiftId: string;
      workJourneyConfigId: string;
      employeeId: string;
    }) => ({
      workShiftId: selection.workShiftId
        ? shiftNameById.get(selection.workShiftId) ??
          optionSourceItems.find(item => item.workShiftId === selection.workShiftId)
            ?.regulationName?.trim() ??
          ''
        : '',
      workJourneyConfigId: selection.workJourneyConfigId
        ? optionSourceItems
            .find(item => item.workJourneyConfigId === selection.workJourneyConfigId)
            ?.journeyName?.trim() ?? ''
        : '',
      employeeId: selection.employeeId
        ? optionSourceItems.find(item => item.employeeId === selection.employeeId)
            ?.userDisplayName?.trim() ?? ''
        : '',
    }),
    [optionSourceItems, shiftNameById],
  );

  const handleStructuralFilterChange = useCallback(
    (
      field:
        | typeof REAL_TIME_FILTER_FIELD.WORK_SHIFT
        | typeof REAL_TIME_FILTER_FIELD.WORK_JOURNEY
        | typeof REAL_TIME_FILTER_FIELD.USER,
      value: string,
    ) => {
      const consistentSelection = resolveConsistentRealTimeSelection(optionSourceItems, {
        workShiftId:
          field === REAL_TIME_FILTER_FIELD.WORK_SHIFT ? value : filterWorkShiftId,
        workJourneyConfigId:
          field === REAL_TIME_FILTER_FIELD.WORK_JOURNEY ? value : filterWorkJourneyConfigId,
        employeeId: field === REAL_TIME_FILTER_FIELD.USER ? value : filterEmployeeId,
      });

      setValues(
        mergeRealTimeStructuralSelection(
          filters,
          consistentSelection,
          getStructuralTranslatedValues(consistentSelection),
          structuralFieldLabels,
        ),
      );
    },
    [
      filterEmployeeId,
      filterWorkJourneyConfigId,
      filterWorkShiftId,
      filters,
      getStructuralTranslatedValues,
      optionSourceItems,
      setValues,
      structuralFieldLabels,
    ],
  );

  useEffect(() => {
    if (elasticLoading || optionSourceItems.length === 0) {
      return;
    }

    const consistentSelection = resolveConsistentRealTimeSelection(optionSourceItems, {
      workShiftId: filterWorkShiftId,
      workJourneyConfigId: filterWorkJourneyConfigId,
      employeeId: filterEmployeeId,
    });

    if (
      consistentSelection.workShiftId === filterWorkShiftId &&
      consistentSelection.workJourneyConfigId === filterWorkJourneyConfigId &&
      consistentSelection.employeeId === filterEmployeeId
    ) {
      return;
    }

    setValues(
      mergeRealTimeStructuralSelection(
        filters,
        consistentSelection,
        getStructuralTranslatedValues(consistentSelection),
        structuralFieldLabels,
      ),
    );
  }, [
    elasticLoading,
    filterEmployeeId,
    filterWorkJourneyConfigId,
    filterWorkShiftId,
    filters,
    getStructuralTranslatedValues,
    optionSourceItems,
    setValues,
    structuralFieldLabels,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeTab,
    filterWorkShiftId,
    filterWorkJourneyConfigId,
    filterEmployeeId,
    filterStatus,
  ]);

  /**
   * Listeners follow Elastic rows for overlay-aware page ids. Overlay hydrate
   * re-filters membership; when those ids (and Elastic objects) are unchanged
   * the previous `items` array is reused so `useFirestoreListSync` does not
   * re-run its effect.
   */
  const pageItemsForSync = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    let pageIds: string[];
    if (activeTab === WORK_JOURNEY_REAL_TIME_TAB.BY_USER) {
      pageIds = overlayAwareFilteredItems.slice(start, start + PAGE_SIZE).map(item => item.id);
    } else {
      const journeyPageRows = overlayAwareJourneyRows.slice(start, start + PAGE_SIZE);
      pageIds = overlayAwareFilteredItems
        .filter(item => journeyPageRows.some(row => matchesJourneyInstance(item, row)))
        .map(item => item.id);
    }

    const itemsById = new Map(dayWindowItems.map(item => [item.id, item]));
    const next = pageIds
      .map(id => itemsById.get(id))
      .filter((item): item is AttendanceDashboardItem => item != null);

    const prev = pageItemsForSyncRef.current;
    if (prev.length === next.length && prev.every((item, index) => item === next[index])) {
      return prev;
    }

    pageItemsForSyncRef.current = next;
    return next;
  }, [
    activeTab,
    currentPage,
    dayWindowItems,
    overlayAwareFilteredItems,
    overlayAwareJourneyRows,
  ]);

  const {
    items: syncedPageItems,
    deletedIds: syncDeletedIds,
    isSyncing,
  } = useFirestoreListSync<AttendanceDashboardItem>({
    items: pageItemsForSync,
    getDocPath: getAttendanceRecordDocPath,
    enabled: !!orgId,
    transform: AttendanceDashboardMapper.mapFirestoreAttendanceRecordOverlay,
  });

  useEffect(() => {
    if (syncDeletedIds.length === 0) {
      return;
    }
    setKnownDeletedIds(prev => {
      let changed = false;
      const next = new Set(prev);
      syncDeletedIds.forEach(id => {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [syncDeletedIds]);

  const syncedEnrichedPageItems = useMemo(
    () =>
      syncedPageItems.map(item => ({
        ...item,
        regulationName: item.workShiftId
          ? regulationNameByShiftId.get(item.workShiftId) ?? item.regulationName ?? null
          : null,
      })),
    [syncedPageItems, regulationNameByShiftId],
  );

  useEffect(() => {
    setFirestoreOverlayById(prev => {
      let changed = false;
      const next = new Map(prev);
      syncedEnrichedPageItems.forEach(item => {
        const current = next.get(item.id);
        const sameOverlay =
          current !== undefined &&
          current.leaveType === item.leaveType &&
          DateUtil.getTimestampMs(current.actualStartAt) ===
            DateUtil.getTimestampMs(item.actualStartAt) &&
          DateUtil.getTimestampMs(current.actualEndAt) ===
            DateUtil.getTimestampMs(item.actualEndAt);

        if (!sameOverlay) {
          next.set(item.id, item);
          changed = true;
        }
      });
      knownDeletedIds.forEach(id => {
        if (next.delete(id)) {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [knownDeletedIds, syncedEnrichedPageItems]);

  /**
   * Firestore is authoritative for rows that have been observed this session.
   * Keep those overlays when the visible page changes (e.g. KPI Absent) so
   * leaveType after a marking still matches Unmarked / Attended / Absent.
   */
  const overlaidDayItems = useMemo(() => {
    const syncedItemsById = new Map(firestoreOverlayById);
    syncedEnrichedPageItems.forEach(item => {
      syncedItemsById.set(item.id, item);
    });
    knownDeletedIds.forEach(id => {
      syncedItemsById.delete(id);
    });
    return mergeSyncedAttendanceItems(dayWindowItems, syncedItemsById);
  }, [dayWindowItems, firestoreOverlayById, knownDeletedIds, syncedEnrichedPageItems]);

  const filteredItems = useMemo(
    () => filterAttendanceItems(overlaidDayItems, attendanceListFilters, staffingByConfigId),
    [attendanceListFilters, overlaidDayItems, staffingByConfigId],
  );

  const kpiItems = useMemo(
    () =>
      applyStructuralFilters(overlaidDayItems, {
        workShiftId: filterWorkShiftId,
        workJourneyConfigId: filterWorkJourneyConfigId,
        employeeId: filterEmployeeId,
      }),
    [filterEmployeeId, filterWorkJourneyConfigId, filterWorkShiftId, overlaidDayItems],
  );
  const kpis = useMemo(
    () => computeAttendanceKpis(kpiItems, staffingByConfigId),
    [kpiItems, staffingByConfigId],
  );
  const journeyRows = useMemo(
    () => buildJourneyAttendanceRows(filteredItems, staffingByConfigId),
    [filteredItems, staffingByConfigId],
  );
  const replacementTargets = useMemo(
    () => buildWorkJourneyReplacementTargets(kpiItems, overlaidDayItems),
    [kpiItems, overlaidDayItems],
  );
  const reportAbsenceUsers = useMemo(
    () =>
      buildReportAbsenceCandidates(
        applyStructuralFilters(overlaidDayItems, {
          workShiftId: filterWorkShiftId,
          workJourneyConfigId: filterWorkJourneyConfigId,
          employeeId: filterEmployeeId,
        }),
      ),
    [
      filterEmployeeId,
      filterWorkJourneyConfigId,
      filterWorkShiftId,
      overlaidDayItems,
    ],
  );

  const totalCount =
    activeTab === WORK_JOURNEY_REAL_TIME_TAB.BY_USER
      ? filteredItems.length
      : journeyRows.length;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);
    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [totalCount, currentPage]);

  const userTableData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [currentPage, filteredItems]);

  const journeyTableData = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return journeyRows.slice(start, start + PAGE_SIZE);
  }, [currentPage, journeyRows]);

  const handleViewJourneyUsers = useCallback(
    (row: JourneyAttendanceGroupRow) => {
      const journeyMembers = overlaidDayItems.filter(item =>
        matchesJourneyInstance(item, row),
      );
      onViewJourneyUsers?.(journeyMembers);
    },
    [onViewJourneyUsers, overlaidDayItems],
  );

  const handleViewMarkedJourneyUsers = useCallback(
    (row: JourneyAttendanceGroupRow) => {
      onViewMarkedJourneyUsers?.(getJourneyInstanceMembers(overlaidDayItems, row));
    },
    [onViewMarkedJourneyUsers, overlaidDayItems],
  );

  const renderStatusBadge = useCallback(
    (status?: AttendanceStatusDisplay | null) => {
      const tag = renderAttendanceStatusTag(intl, status);
      if (!tag) {
        return null;
      }
      return <div className="gx-d-flex gx-justify-content-center">{tag}</div>;
    },
    [intl],
  );
  const identifierLabel = intl.formatMessage({
    id: countryRulesForm?.identifier?.label ?? 'country.chile.identifier',
  });

  const userColumns: ResponsiveTableColumn<AttendanceDashboardItem>[] = useMemo(
    () => [
      {
        key: 'user',
        title: intl.formatMessage({ id: 'general.user' }),
        dataIndex: 'userDisplayName',
        render: (record: AttendanceDashboardItem) => <>{record.userDisplayName}</>,
      },
      {
        key: 'idCard',
        title: identifierLabel,
        dataIndex: 'userIdCard',
        render: (record: AttendanceDashboardItem) => {
          if (!record.userIdCard) {
            return '—';
          }
          return (
            countryRulesForm?.identifier?.formatIdentifier(record.userIdCard) ?? record.userIdCard
          );
        },
      },
      {
        key: 'journey',
        title: intl.formatMessage({ id: 'workJourney.columns.journey' }),
        dataIndex: 'journeyName',
        render: (record: AttendanceDashboardItem) => <>{record.journeyName}</>,
      },
      {
        key: 'scheduledStart',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.scheduledStart' }),
        dataIndex: 'scheduledStartAt',
        align: 'center',
        render: (record: AttendanceDashboardItem) =>
          DateUtil.toTimeString(
            record.scheduledStartAt,
            DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT,
            DateUtil.getDefaultTimeZone(),
          ),
      },
      {
        key: 'journeyStart',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.journeyStart' }),
        dataIndex: 'actualStartAt',
        align: 'center',
        render: (record: AttendanceDashboardItem) =>
          DateUtil.toTimeString(
            record.actualStartAt,
            DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT,
          ),
      },
      {
        key: 'journeyEnd',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.journeyEnd' }),
        dataIndex: 'actualEndAt',
        align: 'center',
        render: (record: AttendanceDashboardItem) =>
          DateUtil.toTimeString(record.actualEndAt, DateUtil.DEFAULT_TIME_WITH_SECONDS_FORMAT),
      },
      {
        key: 'delay',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.delay' }),
        dataIndex: 'scheduledStartAt',
        align: 'center',
        render: (record: AttendanceDashboardItem) => (
          <WorkJourneyArrivalDelayCell
            scheduledStartAtSeconds={timestampSecondsOrNull(record.scheduledStartAt)}
            actualStartAtSeconds={timestampSecondsOrNull(record.actualStartAt)}
          />
        ),
      },
      {
        key: 'state',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.state' }),
        dataIndex: 'state',
        align: 'center',
        render: (record: AttendanceDashboardItem) =>
          renderStatusBadge(resolveUserRowAttendanceState(toUserStatusInput(record))),
      },
      {
        key: 'action',
        title: '',
        dataIndex: 'id',
        align: 'center' as const,
        columnType: 'actions',
        actionsLayout: 'center',
        width: 120,
        render: (record: AttendanceDashboardItem) => (
          <WorkJourneyRealTimeUserActions
            record={record}
            onMarkUser={onMarkUser}
            onReportAbsence={onReportAbsence}
            onReplaceUser={onReplaceUser}
          />
        ),
      },
    ],
    [
      intl,
      renderStatusBadge,
      countryRulesForm,
      identifierLabel,
      onMarkUser,
      onReportAbsence,
      onReplaceUser,
    ],
  );

  const journeyColumns: ResponsiveTableColumn<JourneyAttendanceGroupRow>[] = useMemo(
    () => [
      {
        key: 'journey',
        title: intl.formatMessage({ id: 'workJourney.columns.journey' }),
        dataIndex: 'journeyName',
        render: (record: JourneyAttendanceGroupRow) => <>{record.journeyName}</>,
      },
      {
        key: 'shift',
        title: intl.formatMessage({ id: 'workJourney.columns.shift' }),
        dataIndex: 'regulationName',
        render: (record: JourneyAttendanceGroupRow) => <>{record.regulationName ?? '—'}</>,
      },
      {
        key: 'assigned',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.assignedMin' }),
        dataIndex: 'assignedCount',
        align: 'center',
        render: (record: JourneyAttendanceGroupRow) => {
          const isBelow = record.assignedCount < record.requiredMembers;
          return (
            <span className={styles.countCell}>
              <span className={isBelow ? 'gx-text-danger' : undefined}>
                {record.assignedCount}/{record.requiredMembers}
              </span>
            </span>
          );
        },
      },
      {
        key: 'marked',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.markedMin' }),
        dataIndex: 'markedCount',
        align: 'center',
        render: (record: JourneyAttendanceGroupRow) => (
          <span className={styles.countCell}>
            <Button
              type="link"
              className={`${styles.markedCountButton} ${
                record.markedCount > 0 ? 'gx-text-primary' : 'gx-text-grey'
              }`}
              aria-label={intl.formatMessage(
                { id: 'workJourney.realTime.markedUsers.ariaLabel' },
                { journey: record.journeyName },
              )}
              onClick={() => handleViewMarkedJourneyUsers(record)}
            >
              {record.markedCount}/{record.requiredMembers}
            </Button>
          </span>
        ),
      },
      {
        key: 'status',
        title: intl.formatMessage({ id: 'workJourney.realTime.column.attendanceStatus' }),
        dataIndex: 'status',
        align: 'center',
        render: (record: JourneyAttendanceGroupRow) => renderStatusBadge(record.status),
      },
      {
        key: 'action',
        title: '',
        dataIndex: 'id',
        align: 'center' as const,
        columnType: 'actions',
        actionsLayout: 'center',
        width: 56,
        render: (record: JourneyAttendanceGroupRow) => (
          <ActionButton
            icon="info"
            tooltipText={intl.formatMessage({ id: 'users.list.info' })}
            tooltipPosition="top"
            onClick={() => handleViewJourneyUsers(record)}
          />
        ),
      },
    ],
    [handleViewJourneyUsers, handleViewMarkedJourneyUsers, intl, renderStatusBadge],
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSetActiveTab = useCallback(
    (tab: WorkJourneyRealTimeTab) => {
      setActiveTab(tab);
      const currentStatus = toFilterString(filters?.[REAL_TIME_FILTER_FIELD.STATUS]?.value);
      if (!currentStatus) {
        return;
      }
      const validCodes = new Set<string>(getRealtimeStatusFilterCodesForTab(tab));
      if (!validCodes.has(currentStatus)) {
        setFilters({
          key: REAL_TIME_FILTER_FIELD.STATUS,
          value: '',
          label: intl.formatMessage({ id: 'workJourney.realTime.filter.status' }),
          valueTranslated: '',
          valueType: 'string',
        });
      }
    },
    [filters, intl, setFilters],
  );

  const activeKpiKey = useMemo(
    () => resolveActiveKpiKey({ activeTab, status: filterStatus }),
    [activeTab, filterStatus],
  );
  const handleSelectKpi = useCallback(
    (key: RealTimeKpiKey) => {
      const { tab, statusCode } = resolveKpiFilterSelection(activeKpiKey, key);
      const selectedKpiFilter = REAL_TIME_KPI_FILTERS.find(
        filter => filter.tab === tab && filter.statusCode === statusCode,
      );
      const statusLabelId = selectedKpiFilter
        ? getAttendanceStatusLabelId(selectedKpiFilter.statusCode)
        : undefined;

      setActiveTab(tab);
      setFilters({
        key: REAL_TIME_FILTER_FIELD.STATUS,
        value: statusCode,
        label: intl.formatMessage({ id: 'workJourney.realTime.filter.status' }),
        valueTranslated: statusLabelId ? intl.formatMessage({ id: statusLabelId }) : '',
        valueType: 'string',
      });
    },
    [activeKpiKey, intl, setFilters],
  );

  /**
   * A Firestore deletion reaches `knownDeletedIds` on the next effect. Keep the
   * page in its loading state during that reconciliation so stale Elastic rows
   * never flash before pagination can backfill with the next live records.
   */
  const isReconcilingDeletedItems = syncDeletedIds.some(id => !knownDeletedIds.has(id));
  const hasPendingStaffingIds =
    isStaffingFetching &&
    workJourneyConfigIds.some(id => !staffingByConfigId.has(id));
  const loading =
    elasticLoading ||
    staffingLoading ||
    hasPendingStaffingIds ||
    isSyncing ||
    isReconcilingDeletedItems;
  const isLoaded = !loading && !isFetching && !isStaffingFetching;
  const refresh = useCallback(() => {
    refetch();
    refetchStaffing();
  }, [refetch, refetchStaffing]);

  return {
    loading,
    isLoaded,
    error,
    isRangeTruncated,
    rangeTotalAvailable,
    rangeMaxRecords: ATTENDANCE_DASHBOARD_MAX_RECORDS_IN_RANGE,
    activeTab,
    setActiveTab: handleSetActiveTab,
    currentPage,
    pageSize: PAGE_SIZE,
    totalCount,
    handlePageChange,
    kpis,
    activeKpiKey,
    handleSelectKpi,
    userColumns,
    journeyColumns,
    userTableData,
    filteredItems,
    dayItems: overlaidDayItems,
    journeyTableData,
    toggleFilters,
    isFilterPanelOpen,
    filters,
    setFilters,
    handleStructuralFilterChange,
    shiftOptions,
    journeyOptions,
    userOptions,
    replacementTargets,
    reportAbsenceUsers,
    handleReportAbsence,
    refresh,
  };
};
